// The application-role vocabulary, and the advisory capabilities derived from
// it. Isomorphic and dependency-free, so both graphs can name the same values
// — the same reason `@/lib/identity` lives here rather than beside the table
// that first declared it (client/docs/architecture.md, "The dependency rule").
//
// This is the BFF's *only* copy of the `spectool.*` strings. Before it, roles
// were read ad hoc — the avatar's tone picker hand-coded them, the PingID
// provider config hand-coded the trade-partner default — with no mirror of
// Python's constants. Those callers consume this module now.
//
// The vocabulary is eight strings — the six named by the role-mapping decision
// (#67), `spectool.migrator` (#311) and `spectool.share_visitor` (ADR-0019) —
// and mirrors `server/app/core/roles.py`; `roles-drift.unit.test.ts` is what
// keeps the two from diverging silently. Roles arrive verbatim from the Entra
// `roles` claim; `spectool.trade_partner` is BFF-assigned for PingID sessions,
// `spectool.system` is synthesized for background actors, and
// `spectool.share_visitor` is synthesized by Python for whoever holds a share
// link. There is no group→role mapping on either side.
//
// Three categories, not two: four of the Entra-declared App Roles mirror the
// legacy DigitalOps security-group tiers and are assigned per environment to
// the matching `LEN-SSO-DOP-*` group; `spectool.migrator` is Entra-declared
// but mirrors no legacy tier and is assigned to the app owner's *user* rather
// than to a group, so it rides the claim in local dev sign-in; the remaining
// three never come from Entra at all.

export const SPECTOOL_ADMIN = "spectool.admin";
export const SPECTOOL_NATIONAL_EDITOR = "spectool.national_editor";
export const SPECTOOL_DIVISION_EDITOR = "spectool.division_editor";
export const SPECTOOL_READONLY = "spectool.readonly";
export const SPECTOOL_MIGRATOR = "spectool.migrator";
export const SPECTOOL_TRADE_PARTNER = "spectool.trade_partner";
export const SPECTOOL_SYSTEM = "spectool.system";
export const SPECTOOL_SHARE_VISITOR = "spectool.share_visitor";

export const ROLES = [
  SPECTOOL_ADMIN,
  SPECTOOL_NATIONAL_EDITOR,
  SPECTOOL_DIVISION_EDITOR,
  SPECTOOL_READONLY,
  SPECTOOL_MIGRATOR,
  SPECTOOL_TRADE_PARTNER,
  SPECTOOL_SYSTEM,
  SPECTOOL_SHARE_VISITOR,
] as const;

export type Role = (typeof ROLES)[number];

/**
 * Narrows a value to a known role.
 *
 * Takes `unknown` for the same reason `isProviderId` does: every caller is
 * narrowing a string that arrived from outside the type system — a claim, a
 * session row, a Python response where `roles` is a plain `list[str]`.
 */
export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/**
 * The capability vocabulary: what the UI is allowed to *show*, never what the
 * system is allowed to *do*. Python use cases remain the real gate (#67 §4),
 * so a capability being true here is never sufficient authorization.
 *
 * For four of the five that means deciding whether a tab, button, or link
 * renders. `canRunDataMigration` (#342) is the exception and the reason the
 * rule is phrased as "never sufficient authorization" rather than "only
 * decides what renders": it fronts a *programmatic* route that no UI reaches,
 * so it renders nothing at all. What it shares with the other four is the part
 * that matters — Python is still the enforcement, and deleting the BFF check
 * changes no outcome.
 *
 * Capabilities are named for what they gate, not for who holds them; that is
 * why `isDigitalOpsAdmin` has no successor here. It retired with the
 * DigitalOps identity.
 *
 * A slice adds a capability when it has a gate to hang on it, never in advance
 * — guessing would be ruling on access that slice has not designed yet. The
 * first two are the shell spec's (#132); the third arrived with the catalog
 * admin surfaces (#256), the fourth with the catalog read (#249), and the
 * fifth with the data-migration re-gate (#311, landed in #342).
 *
 * A capability that fronts a Python gate must mirror **exactly** the roles that
 * gate admits, no more: a BFF check stricter than Python's is the BFF deciding
 * authorization, which `client/docs/architecture.md` ("Authentication &
 * authorization", condition 1) forbids — deleting the BFF check has to change
 * no outcome. `roles-drift.unit.test.ts` pins each such capability to the
 * Python role set it mirrors.
 */
export const CAPABILITIES = [
  /** The Admin section — gated on this *and* the `enable_admin_tab` flag. */
  "canAdministrate",
  /**
   * Spec authoring affordances and the lifecycle verbs' server functions — the
   * source's `isSpecsEditor`. Mirrors Python's `SPEC_WRITE_ROLES`
   * (`app/use_cases/specifications/write_access.py`), pinned by
   * `spec-write-access-drift.unit.test.ts`. Necessary, not sufficient: a
   * division editor's scope — base and community specs in their Editable
   * divisions, never a core — is decided server-side per spec (ADR-0011) and
   * answered on each row and summary as `mayEdit`.
   *
   * Also the community writes' gate — hide/unhide (#752), the resource links
   * (#754) — the source's `canToggleVisibility`, which is the same group
   * check. Mirrors Python's `COMMUNITY_WRITE_ROLES`
   * (`app/use_cases/communities/write_access.py`), pinned by
   * `community-write-access-drift.unit.test.ts`. Necessary there too: the
   * division editor's community scope (ADR-0016 decision 9) is Python's,
   * answered per row as `mayToggleVisibility`.
   */
  "canEditSpecs",
  /**
   * The Core tab's "Create Core Specification" button — the source's
   * `isCoreSpecsEditor` (`usePermissions.ts`), the admin and the national
   * editor. Narrower than `canEditSpecs`, the source's `isSpecsEditor`, which
   * also admits a division editor: a division editor never edits a core spec
   * (ADR-0011), so the Core tab offers them no create.
   *
   * Mirrors Python's `SPEC_CORE_CREATE_ROLES`, the create verb's narrowing for
   * a core (`app/use_cases/specifications/write_access.py`), pinned by
   * `spec-write-access-drift.unit.test.ts`.
   */
  "canCreateCoreSpecs",
  /**
   * Catalog lookup writes — creating, renaming and deleting manufacturers,
   * materials and finishes. Mirrors Python's `CATALOG_EDIT_ROLES`
   * (`app/use_cases/catalog/roles.py`): every editor reaches these verbs, from
   * the Admin section *or* inline on the item edit form, so this is
   * deliberately wider than `canAdministrate`. Tier administration is
   * admin-only (ADR-0005 §8) and stays on `canAdministrate`.
   */
  "canEditCatalogLookups",
  /**
   * Writing catalog items — requesting a new one, editing, and the item verbs
   * behind the details page.
   *
   * Mirrors Python's `CATALOG_EDIT_ROLES` (`app/use_cases/catalog/write_access.py`),
   * pinned by `catalog-write-access-drift.unit.test.ts`. It is deliberately a
   * second capability rather than a reading of `canEditCatalogLookups`: the two
   * sets hold the same three roles today but front different gates — the lookup
   * and category verbs there, the item verbs here — and a divergence in either
   * must fail its own pin instead of silently taking the other with it.
   *
   * Membership is necessary and not sufficient. A division editor still faces
   * the per-item division narrowing, which needs an item to evaluate against and
   * so lives in Python's write verbs — and comes back as `mayEdit` on the item
   * detail read, which is what the details menu and the edit page gate on
   * (#621); a *new* item is used by no division, which is why requesting one is
   * exactly this set.
   */
  "canWriteCatalogItems",
  /**
   * Reading the item catalog — the browse, details, and category-tree verbs,
   * and the tier list (Python `CATALOG_TIER_READ_ROLES`, pinned equal).
   *
   * Mirrors Python's `CATALOG_READ_ROLES` (`app/use_cases/catalog/read_access.py`),
   * which is the enforcement; this is the BFF's defense-in-depth reading of the
   * same question, pinned by `catalog-read-access-drift.unit.test.ts`. It is a
   * capability rather than a privilege: every *tier* role has it, up to and
   * including `spectool.readonly`, whose whole purpose is signing in to view.
   * The roles that do not are the ones with no editorial or viewing business
   * here at all — the trade partner and the system actor, which reach catalog
   * data another way or not at all, and `spectool.migrator`, whose sole
   * concern is the data-migration APIs (#311).
   */
  "canBrowseCatalog",
  /**
   * Reading the Spec list — the list page read and its distinct-values read.
   *
   * Mirrors Python's `SPEC_READ_ROLES`
   * (`app/use_cases/specifications/read_access.py`), which is the enforcement;
   * this is the BFF's defense-in-depth reading of the same question, pinned by
   * `spec-read-access-drift.unit.test.ts`. A second capability rather than a
   * reading of `canBrowseCatalog`: the two sets hold the same four tier roles
   * today but front different gates, and a divergence in either must fail its
   * own pin instead of silently taking the other with it.
   */
  "canBrowseSpecs",
  /**
   * The items list's batch mode and the three bulk verbs behind it — Mark as
   * Core, Remove Core Status and Delete. The tier read the Mark as Core
   * dialog's multi-select needs is `canBrowseCatalog`'s (#964 N1).
   *
   * Mirrors Python's `CATALOG_BULK_ROLES` (`app/use_cases/catalog/write_access.py`),
   * and sits inside `CATALOG_TIER_READ_ROLES` — which every catalog reader
   * holds since #895 — so the dialog's tier read always answers. Two roles,
   * because a batch spans a page, so the edit ladder's per-item division
   * narrowing has no single item to narrow on, and the verbs therefore belong to
   * the two roles the narrowing never applies to. Deliberately narrower than
   * `canEditCatalogLookups`, which admits a division editor. Pinned by
   * `catalog-bulk-access-drift.unit.test.ts`.
   *
   * It is the modsy toolbar's own `isItemAdmin` — `isDigitalOpsAdmin ||
   * isNationalEditor` (`items-list-page.tsx`) — under a name that says what it
   * gates rather than who holds it.
   */
  "canBulkEditCatalogItems",
  /**
   * Setting an item's **Internal Use Only** fields — the item edit page's Core,
   * Not Recommended, Status and EI-tier controls.
   *
   * Admin only, and a *seventh* capability rather than a reading of
   * `canBulkEditCatalogItems`, which admits a national editor. The two are one
   * privilege in #238 and were split by an owner ruling (2026-08-26): the
   * items-list toolbar's Mark as Core is `isItemAdmin` in modsy — admin or
   * national editor — while the edit page's whole Internal Use Only block is
   * gated on `isDigitalOpsAdmin` alone (`AssetEditForm.tsx:341`). Two screens,
   * two gates, so two capabilities.
   *
   * It fronts the narrower of the two Python gates the block's fields sit
   * behind — `assert_may_set_internal_use_only`, which is admin-only and covers
   * Not Recommended and Status. Core and the tiers additionally pass
   * `assert_may_set_core_status`, which is wider; rendering the block on the
   * narrower one is what keeps the affordance inside what the server permits
   * for *every* field in it. Pinned by
   * `catalog-internal-use-only-drift.unit.test.ts`.
   */
  "canSetItemInternalUseOnly",
  /**
   * Running the data-migration loader — the programmatic routes in
   * `@/api/data-migration-routes`, and nothing else. Unlike its five siblings
   * this capability renders nothing: no tab, no button, no link hangs on it.
   * It exists because a BFF check that fronts a Python gate has to mirror that
   * gate's roles exactly, and the loader's gate is not `canAdministrate`'s.
   *
   * Mirrors Python's `DATA_MIGRATION_ROLES`
   * (`app/use_cases/catalog_data_migration/capability.py`), which since #311 is
   * `{spectool.migrator}` alone — an administrator no longer reaches the
   * loader. Pinned by `roles-drift.unit.test.ts`.
   */
  "canRunDataMigration",
  /**
   * The Item Catalog's "Upload Target Cost Data" card (#1213) — the source's
   * `isDigitalOpsAdmin` gate on it (`items-filter-sidebar.tsx:175`), under
   * Atlas's role name (PB-1). The card also needs `enable_target_cost_upload`.
   *
   * Mirrors Python's target-cost `LOAD_ROLES`
   * (`app/use_cases/target_costs/uploads.py`), the gate on all four routes the
   * card calls, pinned by `roles-drift.unit.test.ts`. A capability of its own
   * rather than a reading of `canAdministrate`, which holds the same one role
   * today but means the Admin section: were that audience ever widened, the
   * card would widen with it silently.
   */
  "canUploadTargetCosts",
] as const;

export type Capability = (typeof CAPABILITIES)[number];

export type Permissions = Record<Capability, boolean>;

/**
 * Deny-by-default. A user in no LEN-SSO-DOP group carries `roles: []` and gets
 * nothing — the recorded parity break (#67 §3); the old stack's ungated reads
 * let group-less users see everything, which we read as accident, not intent.
 *
 * Frozen because it is one shared instance serving three cases at once (zero
 * roles, an unresolved identity, and the roles that grant nothing), so a
 * caller mutating it would flip deny-by-default for all of them.
 */
export const NO_PERMISSIONS: Permissions = Object.freeze({
  canAdministrate: false,
  canEditSpecs: false,
  canCreateCoreSpecs: false,
  canEditCatalogLookups: false,
  canWriteCatalogItems: false,
  canBrowseCatalog: false,
  canBrowseSpecs: false,
  canBulkEditCatalogItems: false,
  canSetItemInternalUseOnly: false,
  canRunDataMigration: false,
  canUploadTargetCosts: false,
});

/**
 * What each role grants. One row per role, so adding a role to the vocabulary
 * forces a decision about what it grants rather than defaulting into someone
 * else's capabilities — and an empty row is that decision, stated.
 *
 * Roles are a set checked any-of, with no highest-privilege collapsing
 * (#67 §2), so a user's capabilities are the union of their rows.
 */
const GRANTS: Record<Role, readonly Capability[]> = {
  [SPECTOOL_ADMIN]: [
    "canAdministrate",
    "canEditSpecs",
    "canCreateCoreSpecs",
    "canEditCatalogLookups",
    "canWriteCatalogItems",
    "canBrowseCatalog",
    "canBrowseSpecs",
    "canBulkEditCatalogItems",
    "canSetItemInternalUseOnly",
    "canUploadTargetCosts",
  ],
  [SPECTOOL_NATIONAL_EDITOR]: [
    "canEditSpecs",
    "canCreateCoreSpecs",
    "canEditCatalogLookups",
    "canWriteCatalogItems",
    "canBrowseCatalog",
    "canBrowseSpecs",
    "canBulkEditCatalogItems",
  ],
  // Which divisions (the Primary division from the Entra claim plus the
  // Division grants, ADR-0011) is enforced server-side, so the affordance is
  // the same as the national editor's. The catalog's own division narrowing is
  // likewise the item write verbs' job (#244): it needs an item to evaluate
  // against, and the lookup vocabulary has none.
  [SPECTOOL_DIVISION_EDITOR]: [
    "canEditSpecs",
    "canEditCatalogLookups",
    "canWriteCatalogItems",
    "canBrowseCatalog",
    "canBrowseSpecs",
  ],
  // Read-only signs in and views everything, which is what the role is for —
  // the catalog included (#249). It writes nothing, so no lookup verb.
  [SPECTOOL_READONLY]: ["canBrowseCatalog", "canBrowseSpecs"],
  // Exactly one capability, and the migrator is the only role that holds it
  // (#311): `canRunDataMigration` fronts Python's `DATA_MIGRATION_ROLES` gate
  // on the loader, which admits this role and no other — `spectool.admin`
  // included. It renders nothing, so the row grants no UI: a migrator-only
  // session gets the ordinary signed-in shell with no Admin tab and no editing
  // affordances, and — being the only internal role without `canBrowseCatalog`
  // — is the one `requireCatalogAccess` (`@/lib/route-guards`) bounces off the
  // Item Catalog that `spectool.readonly` reaches.
  [SPECTOOL_MIGRATOR]: ["canRunDataMigration"],
  // The external and background identities stay empty, and each empty row is a
  // stated decision: trade partners reach catalog data through the anonymous
  // public product page and its own trimmed DTOs, never through the authed
  // reads; nothing renders for a system actor, and background work reads its
  // repositories directly rather than borrowing a user-facing verb.
  [SPECTOOL_TRADE_PARTNER]: [],
  [SPECTOOL_SYSTEM]: [],
  // A share visitor never holds a BFF session at all — Python resolves it from
  // the token on the public pages (ADR-0019 decision 8) — so nothing renders
  // for it, and the empty row says so.
  [SPECTOOL_SHARE_VISITOR]: [],
};

/**
 * The whole derivation: roles in, advisory capabilities out. Pure, so it is
 * testable without React and reusable from anywhere either graph can reach.
 *
 * Unrecognized strings are ignored rather than rejected — the claim is another
 * system's output, and a role we do not know about grants nothing anyway.
 */
export function derivePermissions(roles: readonly string[]): Permissions {
  const granted = new Set(roles.filter(isRole).flatMap((role) => GRANTS[role]));
  return Object.fromEntries(
    CAPABILITIES.map((capability) => [capability, granted.has(capability)]),
  ) as Permissions;
}
